import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import type { LucideIcon } from 'lucide-react';
import {
  Home,
  Menu,
  User,
  LogOut,
  Moon,
  Heart,
  Users,
  Smartphone,
  GitBranch,
  Clock
} from 'lucide-react';

interface MenuItem {
  icon: LucideIcon;
  label: string;
  path: string;
  roles?: ('administrator' | 'owner' | 'operator')[];
}

const menuItems: MenuItem[] = [
  { icon: User, label: "User Info", path: "" },
  { icon: Home, label: "Dashboard", path: "/" },
  { icon: Moon, label: "Sleep Monitoring", path: "/sleep-monitoring" },
  { icon: Heart, label: "Vital Signs", path: "/vital-signs" },
  { icon: Users, label: "Employee Management", path: "/employees", roles: ['administrator', 'owner', 'operator'] },
  { icon: Smartphone, label: "Device Management", path: "/devices" },
  { icon: GitBranch, label: "Group / Divisi", path: "/group-management", roles: ['administrator', 'owner', 'operator'] },
  { icon: Clock, label: "Shift Settings", path: "/shift-settings" },
  { icon: User, label: "User Management", path: "/user-management", roles: ['administrator', 'owner', 'operator'] },
  { icon: LogOut, label: "Logout", path: "/logout" },
];

/**
 * Helper function to check if user has access to menu item
 * @param item - The menu item
 * @param userRole - The user's role
 * @returns true if user can see this menu item
 */
const canAccessMenuItem = (item: MenuItem, userRole?: string): boolean => {
  // If no roles specified, everyone can access
  if (!item.roles) {
    return true;
  }
  // If roles specified, check if user's role is in the list
  return userRole ? item.roles.includes(userRole as any) : false;
};

/**
 * BottomNav component for mobile navigation
 * Displays fixed bottom navigation bar with menu panel
 * Only visible on mobile devices (hidden on desktop via md:hidden)
 * 
 * Features:
 * - Dashboard quick access
 * - Expandable menu panel with all navigation items
 * - Shift settings quick access
 * - Active route highlighting
 * - User info display
 * - Logout functionality
 * - Role-based menu filtering
 */
export const BottomNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Filter menu items based on user role
  const visibleMenuItems = menuItems.filter(item => canAccessMenuItem(item, user?.role));

  return (
    <div className="md:hidden fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 z-30">
      <div className="flex justify-around items-center h-16">
        <button
          onClick={() => handleNavigation('/')}
          className="flex flex-col items-center"
        >
          <Home size={24} strokeWidth={1.5} />
          <span className="text-xs">Dashboard</span>
        </button>
        <button
          onClick={toggleMenu}
          className="flex flex-col items-center"
        >
          <Menu size={24} strokeWidth={1.5} />
          <span className="text-xs">Menu</span>
        </button>
        <button
          onClick={() => handleNavigation('/user-management')}
          className="flex flex-col items-center"
        >
          <User size={24} strokeWidth={1.5} />
          <span className="text-xs">Profile</span>
        </button>
      </div>

      {isMenuOpen && (
        <div className="absolute bottom-16 left-0 w-full bg-white border-t border-gray-200">
          <nav className="p-4">
            <ul className="space-y-2">
              {visibleMenuItems.map((item) => (
                <li key={item.label}>
                  {item.label === 'Logout' ? (
                    <button
                      onClick={handleLogout}
                      className="w-full text-left flex items-center p-3 rounded-lg text-red-600 hover:bg-gray-50"
                    >
                      <span className="mr-3">
                        <item.icon size={20} strokeWidth={1.5} />
                      </span>
                      {item.label}
                    </button>
                    ) : item.label === 'User Info' ? (
                    <div className="flex items-center p-3 rounded-lg text-gray-400">
                      <span className="mr-3">
                        <item.icon size={20} strokeWidth={1.5} />
                      </span>
                      <div className="text-sm text-gray-400">
                        <span className="text-sm font-medium text-gray-400">
                          {user?.name || user?.email || 'User'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full text-left flex items-center p-3 rounded-lg ${
                        location.pathname === item.path
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <span className="mr-3">
                        <item.icon size={20} strokeWidth={1.5} />
                      </span>
                      {item.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};
